import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { clsx } from 'clsx';
import { VALUE_CHAIN_MAP } from 'constants/general';

import { BUSINESS_UNIT_TYPES } from '../../../../../../constants/interfaces';
import { getUnitHeight } from '../../../../../../utils/reactFlowHelpers';
import UnitStatuses from './UnitStatuses';

import type { ICustomUnitContainer } from '../interfaces/ICustomUnitContainer';

import styles from '../sass/CustomUnitNode.module.scss';

function CustomUnitContainer({
  businessUnitType,
  type,
  estimated,
  childrenCount,
  children,
  isChild,
  totalChildrenHeight,
  depth,
  parentWidth,
  isNestedDropTarget,
  nestedPlaceholderHeight,
  isCollapsed,
  isAIMetadataHighlighted,
  areHandlesHidden,
  showMonitoringRiskBadge = false,
}: ICustomUnitContainer) {
  // Calculate proper height for parent cards with nested children
  const getContainerStyle = () => {
    if (isCollapsed) {
      return {
        minHeight: 'auto',
        overflow: 'hidden',
      };
    }

    // getUnitHeight includes LOGICAL_STATUS_HEIGHT for logical/estimated units,
    // but the statuses badge is rendered outside the card, so the card itself
    // must not reserve that extra height
    const hasStatuses = businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || Boolean(estimated);
    const statusesHeight = hasStatuses ? VALUE_CHAIN_MAP.LOGICAL_STATUS_HEIGHT : 0;
    const baseHeight = getUnitHeight(type, businessUnitType, estimated) - statusesHeight;

    // Calculate total height including children
    // Check totalChildrenHeight > 0 to handle drag preview (tempTotalChildrenHeight)
    // even when childrenCount is still 0 during drag
    if (!isCollapsed && totalChildrenHeight && totalChildrenHeight > 0) {
      // UNIT_RECORD_PADDING: gap between parent content and first child
      // UNIT_CONTAINER_MARGIN_BOTTOM: space for last child's margin so it doesn't overflow the card
      const totalHeight = baseHeight
        + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING
        + totalChildrenHeight
        + VALUE_CHAIN_MAP.UNIT_CONTAINER_MARGIN_BOTTOM;
      return {
        minHeight: `${totalHeight}px`,
        overflow: 'visible',
      };
    }

    return {
      minHeight: `${baseHeight}px`,
      // Header-only cards must clip the square header corners inside the rounded
      // card border; keep overflow visible while a nested drop placeholder is shown
      overflow: isNestedDropTarget ? 'visible' : 'hidden',
    };
  };

  // Calculate available width based on depth. Depth is the only reliable
  // nesting marker: data.parentId may come as 0 even for nested units, and the
  // React Flow parentId prop is set for root cards too (their parent is the swimlane)
  const getContainerWidth = () => {
    // Prefer the rendered parent card width: the card stays UNIT_DEPTH_PADDING
    // narrower than its actual parent even when data.depth is stale (e.g. right
    // after drag-and-drop re-nesting, before the layout is rebuilt)
    if (parentWidth) {
      return { width: `${Math.max(parentWidth - VALUE_CHAIN_MAP.UNIT_DEPTH_PADDING, 100)}px` };
    }

    if (!depth) return undefined;
    // Fallback while the parent is not measured yet: linear indentation, every
    // level is UNIT_DEPTH_PADDING narrower (5px side gap each side)
    const depthIndentation = VALUE_CHAIN_MAP.UNIT_DEPTH_PADDING * depth;
    const availableWidth = VALUE_CHAIN_MAP.SWIMLANE_WIDTH - VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING - depthIndentation;

    return { width: `${Math.max(availableWidth, 100)}px` };
  };

  const containerStyle = getContainerWidth();

  return (
    <div
      className={clsx(styles.container, { [styles.childContainer]: isChild })}
      style={containerStyle}
    >
      {!areHandlesHidden ? (
        <>
          <Handle
            type="source"
            position={Position.Right}
            className={clsx(
              styles.connectionHandle,
              {
                [styles.connectionHandleLogical]:
                  businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || Boolean(estimated),
                [styles.connectionHandlePhysical]:
                  businessUnitType
                  && businessUnitType !== BUSINESS_UNIT_TYPES.LOGICAL
                  && !estimated,
              },
            )}
          />
          <Handle
            type="target"
            position={Position.Left}
            className={clsx(
              styles.connectionHandle,
              {
                [styles.connectionHandleLogical]:
                  businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || Boolean(estimated),
                [styles.connectionHandlePhysical]:
                  businessUnitType
                  && businessUnitType !== BUSINESS_UNIT_TYPES.LOGICAL
                  && !estimated,
              },
            )}
          />
          <div
            className={clsx(
              styles.connectionHandleDot,
              styles.connectionHandleDotRight,
              {
                [styles.connectionHandleDotLogical]:
                  businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || Boolean(estimated),
              },
            )}
            aria-hidden
          />
          <div
            className={clsx(
              styles.connectionHandleDot,
              styles.connectionHandleDotLeft,
              {
                [styles.connectionHandleDotLogical]:
                  businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || Boolean(estimated),
              },
            )}
            aria-hidden
          />
        </>
      ) : null}
      {businessUnitType || estimated ? (
        <UnitStatuses type={type} estimated={estimated} businessUnitType={businessUnitType} />
      ) : null}
      <div
        className={clsx(
          styles.unit,
          styles[type],
          { [styles.aiMetadataHighlighted]: isAIMetadataHighlighted },
          { [styles.monitoringRiskOutline]: showMonitoringRiskBadge },
          { [styles.hasChildren]: !isChild && childrenCount && childrenCount > 0 },
        )}
        style={getContainerStyle()}
        data-depth={depth ?? 0}
      >
        {children}
        {showMonitoringRiskBadge ? (
          <span className={styles.monitoringRiskBadge} aria-hidden="true">!</span>
        ) : null}
        {isNestedDropTarget ? (
          <div
            className={styles.innerDropPlaceholder}
            style={nestedPlaceholderHeight ? { minHeight: `${nestedPlaceholderHeight}px` } : undefined}
            aria-hidden
          />
        ) : null}
      </div>
    </div>
  );
}

export default CustomUnitContainer;
